import React, { FC, useEffect, useMemo, useReducer, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, TextField, Checkbox, FormControlLabel, LinearProgress, Tooltip, IconButton } from '@mui/material';
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { PruDatePicker, PruDateTimePicker } from 'src/app/common/components/PruDatePicker';
import { useCommonStyles } from '../../../../../common/styles/common-styles';
import { DATE_ERROR_TEXT, INCENTIVE_BASE_PATH, MANDATORY_FIELD_ERROR_TEXT } from '../../../constants';
import {
  IncentiveType,
  AwardScheme,
  EnrolmentType,
  IncentiveAward,
  IncentiveDetail,
  IncentiveHighlight,
  PublishStatus,
  HighlightFieldType,
  IncentiveRule,
  IncentiveRuleType,
  AwardOperatorType,
} from '../../../types/incentive-types';
import IncentiveAwardTable from './IncentiveAwardTable';
import TargetApplicationSection from './TargetApplicantSection';
import moment from 'moment';
import { useIntl } from 'react-intl';
import InfoIcon from '@mui/icons-material/Info';
import { IncentiveFormMode } from '../../../utils/common-utils';
import {
  createNewIncentive,
  modifyIncentive,
  publishIncentive,
  unpublishIncentive,
} from '../../../network/incentiveCrud';
import { useDispatch } from 'react-redux';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import DisplaySettingTable from './DisplaySettingTable';
import { createBlob, getBlob } from 'src/app/common/network';
import { numberSeparator, removeSeparator, preFillZeroList, fileUpload, useJWT } from 'src/app/common/utils';
import MiniIncentiveAwardTable from './MiniIncentiveAwardTable';
import { getDayStart, getDayEnd } from '../../../../../common/utils/time-utils';
import { useErrorHandler, ErrorFieldType } from '../../../../../common/utils/form-error-utils';
import TargetApplicationSection_MO_BROKER from './TargetApplicantSection_MO_BROKER';

type IncentiveDetailFormProps = {
  code?: string;
  formMode: IncentiveFormMode;
  incentiveDetail?: IncentiveDetail;
  lastFileName: Pick<IncentiveDetail, 'bulletinFileName' | 'agentListFileName' | 'resultFileName'>;
  onReload: () => void;
  onRouteTo: (route: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  loadingContainer: {
    width: '100%',
    padding: 50,
    display: 'flex',
    justifyContent: 'center',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  textAreaRowContainer: {
    display: 'flex',
  },
  textAreaFieldContainer: {
    paddingTop: 15,
    minWidth: 160,
    boxSizing: 'border-box',
  },
  textArea: {
    lineHeight: 1.5,
    minHeight: 40,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  subHeader: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  fieldContainer: {
    width: 160,
    boxSizing: 'border-box',
  },
  checkboxFieldContainer: {
    paddingTop: 9,
    minWidth: 160,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  mandatory: {
    color: 'red',
  },
  inputMargin: {
    marginRight: 20,
  },
  dateDivider: {
    marginLeft: 8,
    marginRight: 8,
  },
  sectionMargin: {
    marginBottom: 15,
  },
  divideMargin: {
    marginBottom: 10,
  },
  normalBox: {
    width: 250,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  settingTable: {
    width: '50%',
  },
}));

export type IncentiveFormState = {
  code?: string;
  name?: string;
  startDate: Date | null;
  endDate: Date | null;
  enrolmentStartDate: Date | null;
  enrolmentEndDate: Date | null;
  archiveDate: Date | null;
  publishDate: Date | null;
  registrationRequired: boolean;
  status?: PublishStatus;
  bulletin?: string;
  bulletinFileId?: string;
  bulletinFileName?: string;
  imageFileId?: string;
  imageFileName?: string;
  imageFileUrl?: string;
  detail?: string;
  objective?: string;
  award?: string;
  quota?: number;
  type: IncentiveType;
  awards?: IncentiveAward[];
  awardScheme: AwardScheme;
  enrolmentType: EnrolmentType;
  eligibleDesignations?: string[];
  eligibleAgents?: string[];
  excludedAgents?: string[];
  displayAll: boolean;
  agentListFileName?: string;
  highlights?: IncentiveHighlight[];
  rules?: IncentiveRule[];
  stickOnTop: boolean;
};

const initialState: IncentiveFormState = {
  code: undefined,
  name: undefined,
  startDate: null,
  endDate: null,
  enrolmentStartDate: null,
  enrolmentEndDate: null,
  archiveDate: null,
  publishDate: null,
  status: undefined,
  registrationRequired: true,
  bulletin: undefined,
  detail: undefined,
  objective: undefined,
  award: undefined,
  quota: undefined,
  type: IncentiveType.INCENTIVE,
  awardScheme: AwardScheme.INCENTIVE,
  enrolmentType: EnrolmentType.BY_SEGMENT,
  awards: undefined,
  eligibleDesignations: undefined,
  eligibleAgents: undefined,
  excludedAgents: undefined,
  displayAll: false,
  highlights: [{ name: '', type: '' }],
  rules: [],
  agentListFileName: undefined,
  stickOnTop: false,
};

const detailToStateConvertor = (detail: IncentiveDetail): IncentiveFormState => {
  const properties = detail.properties || [];
  const eligibleDesignations =
    detail.eligibleSegments && detail.eligibleSegments.designation ? detail.eligibleSegments.designation : [];
  let displayAllField = properties.find((property) => property.key === 'displayAll');
  const displayAll = displayAllField ? (displayAllField.value === 'true' ? true : false) : false;
  return {
    code: detail.code,
    name: detail.name,
    startDate: detail.startDate,
    endDate: detail.endDate,
    enrolmentStartDate: detail.enrolmentStartDate || null,
    enrolmentEndDate: detail.enrolmentEndDate || null,
    archiveDate: detail.archiveDate,
    publishDate: detail.publishDate,
    registrationRequired: detail.registrationRequired !== undefined ? detail.registrationRequired : true,
    status: detail.status,
    bulletin: detail.bulletin,
    bulletinFileId: detail.bulletinFileId,
    bulletinFileName: detail.bulletinFileName,
    imageFileId: detail.incentiveImage?.blobId || '',
    imageFileName: detail.incentiveImage?.filename || '',
    imageFileUrl: detail.incentiveImage?.url || '',
    detail: detail.detail,
    objective: detail.objective,
    award: detail.award,
    quota: detail.quota,
    type: detail.type,
    awardScheme: detail.awardScheme,
    enrolmentType: detail.enrolmentType,
    awards: detail.awards,
    eligibleDesignations,
    eligibleAgents: detail.eligibleAgents,
    excludedAgents: detail.excludedAgents,
    displayAll,
    rules: detail.rules
      ? detail.rules.map((rule) => {
          if (rule.type === IncentiveRuleType.CURRENCY && rule.values.length > 0) {
            rule.values = [numberSeparator(rule.values[0])];
          }
          return rule;
        })
      : [],
    agentListFileName: detail.agentListFileName,
    highlights:
      detail.highlights && detail.highlights.length === 0 ? [{ name: '', type: '' }] : detail.highlights || [],
    stickOnTop: detail.stickOnTop !== undefined ? detail.stickOnTop : false,
  };
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof IncentiveFormState;
    value: any;
  };
};

type ToggleDesignationAction = {
  type: 'TOGGLE_DESIGNATION';
  payload: {
    value: string;
  };
};

type AddHighlightRowAction = {
  type: 'ADD_HIGHLIGHT_ROW';
};

type MoveHighlightRowAction = {
  type: 'MOVE_HIGHLIGHT_ROW';
  payload: {
    from: number;
    to: number;
  };
};

type ModifyHighlightRowAction = {
  type: 'MODIFY_HIGHLIGHT_ROW';
  payload: {
    index: number;
    field: keyof IncentiveHighlight;
    value: string;
  };
};

type DeleteHighlightRowAction = {
  type: 'DELETE_HIGHLIGHT_ROW';
  payload: {
    index: number;
  };
};

type SwitchIncentiveTypeAction = {
  type: 'SWITCH_INCENTIVE_TYPE';
  payload: {
    type: IncentiveType;
  };
};

type IncentiveFormAction =
  | ModifyFieldAction
  | ToggleDesignationAction
  | SwitchIncentiveTypeAction
  | AddHighlightRowAction
  | MoveHighlightRowAction
  | ModifyHighlightRowAction
  | DeleteHighlightRowAction;

const incentiveFormReducer = (state: IncentiveFormState, action: IncentiveFormAction): IncentiveFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'TOGGLE_DESIGNATION':
      let newDesignation = [...(state.eligibleDesignations || [])];
      let itemIndex = newDesignation.findIndex((designation) => designation === action.payload.value);
      newDesignation[itemIndex] ? newDesignation.splice(itemIndex, 1) : newDesignation.push(action.payload.value);
      return {
        ...state,
        eligibleDesignations: newDesignation,
      };
    case 'SWITCH_INCENTIVE_TYPE':
      const isIncentive = action.payload.type === IncentiveType.INCENTIVE;
      return {
        ...state,
        type: isIncentive ? IncentiveType.INCENTIVE : IncentiveType.MINI_INCENTIVE,
        awardScheme: isIncentive ? AwardScheme.INCENTIVE : AwardScheme.MINI_INCENTIVE,
        registrationRequired: true,
        highlights: isIncentive ? [{ name: '', type: '' }] : [{ name: 'Rank', type: HighlightFieldType.RANK }],
        rules: [],
        awards: [],
        displayAll: false,
      };
    case 'ADD_HIGHLIGHT_ROW':
      return {
        ...state,
        highlights: state.highlights ? [...state.highlights, { name: '', type: '' }] : [{ name: '', type: '' }],
      };
    case 'MODIFY_HIGHLIGHT_ROW':
      if (state.highlights && state.highlights[action.payload.index]) {
        let item = state.highlights[action.payload.index];
        item[action.payload.field] = action.payload.value;
      }
      return { ...state };
    case 'MOVE_HIGHLIGHT_ROW':
      if (state.highlights) {
        let highlights = state.highlights;
        highlights.splice(action.payload.to, 0, highlights.splice(action.payload.from, 1)[0]);
      }
      return { ...state };
    case 'DELETE_HIGHLIGHT_ROW':
      if (state.highlights && state.highlights[action.payload.index]) {
        let highlightArr = state.highlights;
        highlightArr.splice(action.payload.index, 1);
      }
      return { ...state };
  }
};

export type ErrorState = {
  mandatory: {
    code: boolean;
    name: boolean;
    startDate: boolean;
    endDate: boolean;
    requirementEmpty: boolean;
    awardFirstRowQuota: boolean;
    awardFirstRowName: boolean;
  };
  immediate: {
    endDateBeforeStartDate: boolean;
    enrolmentEndDateBeforeStartDate: boolean;
  };
};

const IncentiveDetailForm: FC<IncentiveDetailFormProps> = ({
  code,
  formMode,
  lastFileName,
  incentiveDetail,
  onReload,
  onRouteTo,
}) => {
  const jwt = useJWT() || {};
  const { region, channel } = jwt;

  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const TranslationWithVariable = (key: string, count: number | string) =>
    intl.formatMessage({ id: key }, { num: count });

  const [uploadProgress, setUploadProgress] = useState<number>();

  const [uploadImageProgress, setUploadImageProgress] = useState<number>();

  const [formState, formDispatch] = useReducer(
    incentiveFormReducer,
    incentiveDetail ? detailToStateConvertor(incentiveDetail) : initialState,
  );

  const isDisabled = useMemo(() => {
    return formState.status && formState.status === PublishStatus.PUBLISHED ? true : false;
  }, [formState.status]);

  const modifyTargetApplicant = (
    option: keyof Pick<
      IncentiveFormState,
      'enrolmentType' | 'eligibleAgents' | 'eligibleDesignations' | 'excludedAgents' | 'agentListFileName'
    >,
    value: any,
  ) => {
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: option, value } });
  };

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler, immediateErrorValidator } =
    useErrorHandler<ErrorState>(formState, [
      {
        name: 'code',
        fieldType: ErrorFieldType.MANDATORY,
      },
      {
        name: 'name',
        fieldType: ErrorFieldType.MANDATORY,
      },
      {
        name: 'startDate',
        fieldType: ErrorFieldType.MANDATORY,
      },
      {
        name: 'endDate',
        fieldType: ErrorFieldType.MANDATORY,
      },
      {
        name: 'requirementEmpty',
        fieldType: ErrorFieldType.MANDATORY,
        condition: () => {
          if (formState.type === IncentiveType.INCENTIVE) {
            return false;
          } else {
            let flag = true;
            if (formState.awards && formState.awards[0]) {
              const requirements = formState.awards[0].requirements;
              flag = !requirements || requirements.length < 1;
            }
            return flag;
          }
        },
      },
      {
        name: 'awardFirstRowQuota',
        fieldType: ErrorFieldType.MANDATORY,
        condition: () => {
          if (formState.type === IncentiveType.INCENTIVE) {
            return false;
          } else {
            let quotaFlag = true;
            if (formState.awards && formState.awards[0]) {
              const properties = formState.awards[0].properties;
              const quota = properties.find((property) => property.key === 'quota');
              if (quota) {
                quotaFlag = !!!quota.value;
              }
            }
            return quotaFlag;
          }
        },
      },
      {
        name: 'awardFirstRowName',
        fieldType: ErrorFieldType.MANDATORY,
        condition: () => {
          if (formState.type === IncentiveType.INCENTIVE) {
            return false;
          } else {
            let nameFlag = true;
            if (formState.awards && formState.awards[0]) {
              const properties = formState.awards[0].properties;
              const name = properties.find((property) => property.key === 'name');
              if (name) {
                nameFlag = !!!name.value;
              }
            }
            return nameFlag;
          }
        },
      },
      {
        name: 'endDateBeforeStartDate',
        fieldType: ErrorFieldType.IMMEDIATE,
        condition: () => {
          if (formState.startDate && formState.endDate) {
            return !!moment(new Date(formState.startDate)).isAfter(moment(new Date(formState.endDate)));
          } else {
            return false;
          }
        },
      },
      {
        name: 'enrolmentEndDateBeforeStartDate',
        fieldType: ErrorFieldType.IMMEDIATE,
        condition: () => {
          if (formState.enrolmentStartDate && formState.enrolmentEndDate) {
            return !!moment(new Date(formState.enrolmentStartDate)).isAfter(
              moment(new Date(formState.enrolmentEndDate)),
            );
          } else {
            return false;
          }
        },
      },
    ]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      try {
        const createBlobRes = await createBlob(
          { mimeType: file.type, accessLevel: 'public', module: 'incentive' },
          dispatch,
        );
        await fileUpload(createBlobRes.url, file, setUploadProgress);
        formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'bulletinFileId', value: createBlobRes.blobId } });
        formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'bulletinFileName', value: file.name } });
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: `Upload file successfully - ${file.name}`,
            },
          ]),
        );
      } catch (err) {
      } finally {
        setUploadProgress(undefined);
      }
    }
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileSize = file.size / 1024 / 1024;
      const errMsg = TranslationWithVariable('global.max.file.size.allow', 5) || '';
      if (fileSize > 5) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: '',
              content: errMsg,
            },
          ]),
        );
        return;
      }

      try {
        const createBlobRes = await createBlob(
          { mimeType: file.type, accessLevel: 'public', module: 'incentive' },
          dispatch,
        );
        await fileUpload(createBlobRes.url, file, setUploadImageProgress);
        const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
        const result = blobDetail[0];
        if (result) {
          formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileUrl', value: result.url } });
        }
        formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileId', value: createBlobRes.blobId } });
        formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileName', value: file.name } });
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: `Upload Image successfully - ${file.name}`,
            },
          ]),
        );
      } catch (err) {
      } finally {
        setUploadImageProgress(undefined);
      }
    }
  };

  const removeImageFile = () => {
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileUrl', value: '' } });
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileId', value: '' } });
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'imageFileName', value: '' } });
    dispatch(
      appendAlertItem([
        {
          severity: AlertType.SUCCESS,
          title: 'Success',
          content: `Remove image file successfully`,
        },
      ]),
    );
  };

  const onUnpublish = async () => {
    if (code) {
      try {
        await unpublishIncentive(code, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: `Unpublished successfully - ${code}`,
            },
          ]),
        );
        onReload();
      } catch (err) {}
    }
  };

  const onSubmitForm = async (mode: PublishStatus) => {
    let { hasError } = onSubmitErrorValidator();
    if (!hasError) {
      // Call Api and submit
      const details: IncentiveDetail = {
        code: formState.code ? formState.code.trim() : '',
        name: formState.name || '',
        startDate: formState.startDate ? getDayStart(formState.startDate) : null,
        endDate: formState.endDate ? getDayEnd(formState.endDate) : null,
        enrolmentStartDate: formState.enrolmentStartDate,
        enrolmentEndDate: formState.enrolmentEndDate,
        archiveDate: formState.archiveDate,
        publishDate: formState.publishDate,
        status: undefined,
        registrationRequired: formState.registrationRequired,
        bulletin: formState.bulletin || '',
        bulletinFileId: formState.bulletinFileId || '',
        bulletinFileName: formState.bulletinFileName || '',
        imageFileId: formState.imageFileId || '',
        imageFileName: formState.imageFileName || '',
        incentiveImage: formState.imageFileId
          ? {
              blobId: formState.imageFileId || '',
              filename: formState.imageFileName || '',
            }
          : undefined,
        detail: formState.detail || '',
        objective: formState.objective || '',
        award: formState.award || '',
        quota: formState.quota ? Number(formState.quota) : undefined,
        type: formState.type,
        awardScheme: formState.awardScheme,
        enrolmentType: formState.enrolmentType,
        eligibleSegments: {
          designation: formState.eligibleDesignations || [],
        },
        awards: formState.awards || [],
        rules: formState.rules
          ? formState.rules.map((rule) => {
              if (rule.type === IncentiveRuleType.CURRENCY) {
                rule.values = [removeSeparator(rule.values[0])];
              }
              return rule;
            })
          : [],
        eligibleAgents: formState.eligibleAgents ? preFillZeroList(formState.eligibleAgents) : [],
        excludedAgents: formState.excludedAgents ? preFillZeroList(formState.excludedAgents) : [],
        properties: [
          {
            key: 'displayAll',
            value: formState.displayAll ? 'true' : 'false',
          },
        ],
        agentListFileName: formState.agentListFileName || '',
        highlights:
          formState.highlights &&
          formState.highlights[0] &&
          !formState.highlights[0].name &&
          !formState.highlights[0].type
            ? []
            : formState.highlights || [],
        stickOnTop: formState.stickOnTop,
      };
      try {
        if (formMode === IncentiveFormMode.CREATE) {
          await createNewIncentive(details, dispatch);
          if (mode === PublishStatus.PUBLISHED) {
            await publishIncentive(details.code, dispatch);
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.SUCCESS,
                  title: 'Success',
                  content: `Record saved and published successfully - ${details.code}`,
                },
              ]),
            );
          } else {
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.SUCCESS,
                  title: 'Success',
                  content: `Record saved successfully - ${details.code}`,
                },
              ]),
            );
          }
        } else if (code && formMode === IncentiveFormMode.EDIT) {
          await modifyIncentive(code, details, dispatch);
          if (mode === PublishStatus.PUBLISHED) {
            await publishIncentive(code, dispatch);
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.SUCCESS,
                  title: 'Success',
                  content: `Record updated and published successfully - ${code}`,
                },
              ]),
            );
          } else {
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.SUCCESS,
                  title: 'Success',
                  content: `Record updated successfully - ${code}`,
                },
              ]),
            );
          }
        }
        onRouteTo(INCENTIVE_BASE_PATH);
      } catch (err) {}
    }
  };

  useEffect(
    () => {
      immediateErrorValidator();
    },
    // eslint-disable-next-line
    [
      formState.startDate,
      formState.endDate,
      formState.enrolmentStartDate,
      formState.enrolmentEndDate,
      formState.archiveDate,
    ],
  );

  const { isEsub, benfCd, afyp, benfMinAFYP } = useMemo(() => {
    if (formState.rules) {
      const isEsub = formState.rules.find((rule) => rule.operand === 'isEsub');
      const benfCd = formState.rules.find((rule) => rule.operand === 'benfCd');
      const afyp = formState.rules.find((rule) => rule.operand === 'afyp');
      const benfMinAFYP = formState.rules.find((rule) => rule.operand === 'benfMinAFYP');
      return {
        isEsub,
        benfCd,
        afyp,
        benfMinAFYP,
      };
    }
    return {
      isEsub: undefined,
      benfCd: undefined,
      afyp: undefined,
      benfMinAFYP: undefined,
    };
  }, [formState.rules]);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {formMode === IncentiveFormMode.CREATE ? 'Add' : isDisabled ? 'View' : 'Edit'} Incentive
            </div>
          </div>
          <Button variant="contained" color="inherit" onClick={() => onRouteTo(INCENTIVE_BASE_PATH)}>
            Back
          </Button>
        </div>

        {/* Basic Information */}
        <div className={classes.sectionMargin}>
          <div className={classes.divideMargin}>
            <span className={classes.subHeader}>Basic Information</span>
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                Incentive Code<span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <TextField
              disabled={isDisabled || formMode === IncentiveFormMode.EDIT}
              error={errorState.mandatory.code}
              className={classes.inputMargin}
              margin="dense"
              variant="outlined"
              helperText={errorState.mandatory.code && MANDATORY_FIELD_ERROR_TEXT}
              value={formState.code}
              onChange={(e) => {
                const onlyAlphNums = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                onDismissErrorHandler('code', onlyAlphNums);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'code', value: onlyAlphNums } });
              }}
            />
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                Incentive Name<span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                disabled={isDisabled}
                error={errorState.mandatory.name}
                margin="dense"
                variant="outlined"
                fullWidth
                helperText={errorState.mandatory.name && MANDATORY_FIELD_ERROR_TEXT}
                value={formState.name}
                onChange={(e) => {
                  onDismissErrorHandler('name', e.target.value);
                  formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'name', value: e.target.value } });
                }}
              />
            </div>
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                Incentive Duration<span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <PruDatePicker
              disabled={isDisabled}
              slotProps={{
                textField: {
                  error: errorState.mandatory.startDate || errorState.immediate.endDateBeforeStartDate,
                  helperText: errorState.mandatory.startDate && MANDATORY_FIELD_ERROR_TEXT,
                },
              }}
              format="DD/MM/YYYY"
              value={formState.startDate}
              onChange={(date) => {
                onDismissErrorHandler('startDate', date);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'startDate', value: date } });
              }}
            />
            <div className={classes.dateDivider}>-</div>
            <PruDatePicker
              disabled={isDisabled}
              slotProps={{
                textField: {
                  error: errorState.mandatory.endDate || errorState.immediate.endDateBeforeStartDate,
                  helperText: errorState.mandatory.endDate
                    ? MANDATORY_FIELD_ERROR_TEXT
                    : errorState.immediate.endDateBeforeStartDate && DATE_ERROR_TEXT,
                },
              }}
              format="DD/MM/YYYY"
              value={formState.endDate}
              onChange={(date) => {
                onDismissErrorHandler('endDate', date);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'endDate', value: date } });
              }}
            />
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Enrolment Duration :</span>
            </div>
            <PruDateTimePicker
              disabled={isDisabled}
              slotProps={{
                textField: {
                  error: errorState.immediate.enrolmentEndDateBeforeStartDate,
                },
              }}
              ampm={false}
              format="DD/MM/YYYY HH:mm"
              value={formState.enrolmentStartDate}
              onChange={(date) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'enrolmentStartDate', value: date } })
              }
            />
            <div className={classes.dateDivider}>-</div>
            <PruDateTimePicker
              disabled={isDisabled}
              slotProps={{
                textField: {
                  error: errorState.immediate.enrolmentEndDateBeforeStartDate,
                  helperText: errorState.immediate.enrolmentEndDateBeforeStartDate && DATE_ERROR_TEXT,
                },
              }}
              ampm={false}
              format="DD/MM/YYYY HH:mm"
              value={formState.enrolmentEndDate}
              onChange={(date) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'enrolmentEndDate', value: date } })
              }
            />
          </div>
        </div>

        {/* Incentive Details */}
        <div className={classes.sectionMargin}>
          <div className={classes.divideMargin}>
            <span className={classes.subHeader}>Incentive Details</span>
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.checkboxFieldContainer}>
              <span className={classes.field}>Incentive Type :</span>
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled || formMode === IncentiveFormMode.EDIT}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.type === IncentiveType.INCENTIVE}
                  onChange={(e) => {
                    if (e.target.checked) {
                      formDispatch({ type: 'SWITCH_INCENTIVE_TYPE', payload: { type: IncentiveType.INCENTIVE } });
                    }
                  }}
                />
              }
              label="Incentive"
              labelPlacement="end"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled || formMode === IncentiveFormMode.EDIT}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.type === IncentiveType.MINI_INCENTIVE}
                  onChange={(e) => {
                    if (e.target.checked) {
                      formDispatch({ type: 'SWITCH_INCENTIVE_TYPE', payload: { type: IncentiveType.MINI_INCENTIVE } });
                    }
                  }}
                />
              }
              label="Mini Incentive"
              labelPlacement="end"
            />
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.checkboxFieldContainer}>
              <span className={classes.field}>Registration Required :</span>
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.registrationRequired}
                  onChange={(e) =>
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'registrationRequired', value: e.target.checked },
                    })
                  }
                />
              }
              label="Yes"
              labelPlacement="end"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={!formState.registrationRequired}
                  onChange={(e) =>
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'registrationRequired', value: !e.target.checked },
                    })
                  }
                />
              }
              label="No"
              labelPlacement="end"
            />
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.textAreaFieldContainer}>
              <span className={classes.field}>Incentive Description :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                disabled={isDisabled}
                InputProps={{
                  classes: {
                    input: classes.textArea,
                  },
                }}
                margin="dense"
                variant="outlined"
                fullWidth
                multiline
                value={formState.detail}
                onChange={(e) =>
                  formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'detail', value: e.target.value } })
                }
              />
            </div>
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.textAreaFieldContainer}>
              <span className={classes.field}>Objective :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                disabled={isDisabled}
                InputProps={{
                  classes: {
                    input: classes.textArea,
                  },
                }}
                margin="dense"
                variant="outlined"
                fullWidth
                multiline
                value={formState.objective}
                onChange={(e) =>
                  formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'objective', value: e.target.value } })
                }
              />
            </div>
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.textAreaFieldContainer}>
              <span className={classes.field}>Bulletin :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                disabled={isDisabled}
                InputProps={{
                  classes: {
                    input: classes.textArea,
                  },
                }}
                margin="dense"
                variant="outlined"
                fullWidth
                multiline
                value={formState.bulletin}
                onChange={(e) =>
                  formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'bulletin', value: e.target.value } })
                }
              />
            </div>
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.textAreaFieldContainer}>
              <span className={classes.field}>
                Award Description
                <Tooltip title="Please fill in approximately 14 Chinese Words / 30 english characters to prevent app display overflow">
                  <IconButton size={'small'} style={{ padding: 5, color: '#BBBBBB' }} disabled={isDisabled}>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
                :
              </span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                disabled={isDisabled}
                InputProps={{
                  classes: {
                    input: classes.textArea,
                  },
                }}
                margin="dense"
                variant="outlined"
                fullWidth
                multiline
                value={formState.award}
                onChange={(e) =>
                  formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'award', value: e.target.value } })
                }
              />
            </div>
          </div>
        </div>

        {/* Incentive Settings */}
        <div className={classes.sectionMargin}>
          <div className={classes.divideMargin}>
            <span className={classes.subHeader}>Incentive Settings</span>
          </div>
          <div style={{ marginBottom: 10 }} className={classes.textAreaRowContainer}>
            <div style={{ paddingTop: 3 }} className={classes.fieldContainer}>
              <span className={classes.field}>Upload image :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <input
                id="upload-image"
                hidden
                type="file"
                accept="image/*"
                disabled={!!uploadImageProgress}
                onChange={handleImageFile}
              />
              <div className={classes.rowContainer}>
                <Button
                  disabled={isDisabled}
                  variant="contained"
                  color="secondary"
                  onClick={() => document.getElementById('upload-image')!.click()}
                >
                  Choose File
                </Button>
                {formState.imageFileUrl ? (
                  <Button
                    style={{ marginLeft: 20 }}
                    disabled={isDisabled}
                    variant="contained"
                    color="secondary"
                    onClick={() => removeImageFile()}
                  >
                    Remove File
                  </Button>
                ) : (
                  <div style={{ marginLeft: 10 }}>No file chosen</div>
                )}
              </div>

              {!!uploadImageProgress && (
                <LinearProgress style={{ marginTop: 10 }} variant="determinate" value={uploadImageProgress} />
              )}
            </div>
          </div>

          <div className={classes.rowContainer} style={{ marginTop: 15 }}>
            <div className={classes.fieldContainer}></div>
            <div style={{ flexGrow: 1 }}>
              <div className={classes.rowContainer}>
                <div>{formState.imageFileName ? formState.imageFileName : ''}</div>
              </div>
              <div className={classes.rowContainer}>
                {formState.imageFileUrl && (
                  <div>
                    <img
                      style={{ width: 'auto', height: '150px', marginBottom: '20px' }}
                      src={formState.imageFileUrl}
                      alt="Incentive"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 10 }} className={classes.textAreaRowContainer}>
            <div style={{ paddingTop: 3 }} className={classes.fieldContainer}>
              <span className={classes.field}>Upload bulletin :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <input
                id="upload-bulletin"
                hidden
                type="file"
                disabled={!!uploadProgress}
                onClick={(e) => {
                  const element = e.target as HTMLInputElement;
                  element.value = '';
                }}
                onChange={handleFile}
              />
              <div className={classes.rowContainer}>
                <Button
                  disabled={isDisabled}
                  variant="contained"
                  color="secondary"
                  onClick={() => document.getElementById('upload-bulletin')!.click()}
                >
                  Choose File
                </Button>
                <div style={{ marginLeft: 10 }}>
                  {formState.bulletinFileName ? formState.bulletinFileName : 'No file chosen'}
                </div>
              </div>
              {!!uploadProgress && (
                <LinearProgress style={{ marginTop: 10 }} variant="determinate" value={uploadProgress} />
              )}
            </div>
          </div>

          {/* Incentive */}
          {formState.type !== IncentiveType.MINI_INCENTIVE && (
            <>
              <div className={classes.textAreaRowContainer}>
                <div className={classes.checkboxFieldContainer}>
                  <span className={classes.field}>Properties :</span>
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isDisabled}
                      icon={<CircleUnchecked />}
                      checkedIcon={<CircleCheckedFilled />}
                      checked={formState.displayAll}
                      onChange={(e) =>
                        formDispatch({
                          type: 'MODIFY_FIELD',
                          payload: { field: 'displayAll', value: e.target.checked },
                        })
                      }
                    />
                  }
                  label="Display All"
                  labelPlacement="end"
                />
              </div>
              <div className={classes.textAreaRowContainer}>
                <div className={classes.checkboxFieldContainer}>
                  <span className={classes.field}>Highlights :</span>
                </div>
                <DisplaySettingTable
                  isDisabled={isDisabled}
                  className={classes.settingTable}
                  highlights={formState.highlights || []}
                  onAddRow={() => formDispatch({ type: 'ADD_HIGHLIGHT_ROW' })}
                  onMoveRow={(from, to) => formDispatch({ type: 'MOVE_HIGHLIGHT_ROW', payload: { from, to } })}
                  onModifyField={(index, field, value) =>
                    formDispatch({ type: 'MODIFY_HIGHLIGHT_ROW', payload: { index, field, value } })
                  }
                  onDeleteRow={(index) => formDispatch({ type: 'DELETE_HIGHLIGHT_ROW', payload: { index } })}
                />
              </div>
              <div className={classes.textAreaRowContainer}>
                <div className={classes.textAreaFieldContainer}>
                  <span className={classes.field}>Award Settings :</span>
                </div>
                <IncentiveAwardTable
                  isDisabled={isDisabled}
                  style={{ marginTop: 10, flexGrow: 1 }}
                  incentiveAward={formState.awards}
                  onUpdate={(result) =>
                    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awards', value: result } })
                  }
                />
              </div>
            </>
          )}

          {/* Mini Incentive */}
          {formState.type === IncentiveType.MINI_INCENTIVE && (
            <>
              <div className={classes.textAreaRowContainer}>
                <div className={classes.checkboxFieldContainer}>
                  <span className={classes.field}>eSubmission Channel :</span>
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isDisabled}
                      icon={<CircleUnchecked />}
                      checkedIcon={<CircleCheckedFilled />}
                      checked={!!!isEsub}
                      onChange={(e) => {
                        if (e.target.checked && formState.rules) {
                          let rules = [...formState.rules];
                          const foundIndex = rules.findIndex((rule) => rule.operand === 'isEsub');
                          if (foundIndex !== -1) {
                            rules.splice(foundIndex, 1);
                            formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'rules', value: rules } });
                          }
                        }
                      }}
                    />
                  }
                  label="All Channels"
                  labelPlacement="end"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isDisabled}
                      icon={<CircleUnchecked />}
                      checkedIcon={<CircleCheckedFilled />}
                      checked={!!isEsub}
                      onChange={(e) => {
                        if (e.target.checked && formState.rules && !isEsub) {
                          formDispatch({
                            type: 'MODIFY_FIELD',
                            payload: {
                              field: 'rules',
                              value: [
                                ...formState.rules,
                                {
                                  operand: 'isEsub',
                                  operator: 'in',
                                  type: IncentiveRuleType.VALUE,
                                  values: ['Y'],
                                },
                              ],
                            },
                          });
                        }
                      }}
                    />
                  }
                  label="eSub Only"
                  labelPlacement="end"
                />
              </div>
              <div className={classes.sectionMargin}>
                <div className={classes.divideMargin}>
                  <span className={classes.subHeader}>Target Settings</span>
                </div>
                <div style={{ marginBottom: 10 }} className={classes.rowContainer}>
                  <div className={classes.fieldContainer}>
                    <span className={classes.field}>Minimum AFYP per policy :</span>
                  </div>
                  <TextField
                    disabled={isDisabled}
                    style={{ margin: 0 }}
                    className={classes.inputMargin}
                    margin="dense"
                    variant="outlined"
                    value={afyp ? afyp.values[0] : ''}
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                      let rules = formState.rules ? [...formState.rules] : [];
                      const benfMinAFYPIndex = rules.findIndex((rule) => rule.operand === 'afyp');
                      if (benfMinAFYPIndex !== -1) {
                        if (!e.target.value) {
                          rules.splice(benfMinAFYPIndex, 1);
                        } else {
                          rules[benfMinAFYPIndex].values = [numberSeparator(onlyNums)];
                        }
                      } else {
                        if (e.target.value) {
                          rules = [
                            ...rules,
                            {
                              operand: 'afyp',
                              operator: AwardOperatorType.GREATER_THAN_EQUAL,
                              type: IncentiveRuleType.CURRENCY,
                              values: [numberSeparator(onlyNums)],
                            },
                          ];
                        }
                      }
                      formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'rules', value: rules } });
                    }}
                  />
                </div>
                <div className={classes.textAreaRowContainer}>
                  <div className={classes.checkboxFieldContainer}>
                    <span className={classes.field}>Benefit Code :</span>
                  </div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isDisabled}
                        icon={<CircleUnchecked />}
                        checkedIcon={<CircleCheckedFilled />}
                        checked={!!!benfCd}
                        onChange={(e) => {
                          if (e.target.checked && formState.rules) {
                            let rules = [...formState.rules];
                            const benfCdIndex = rules.findIndex((rule) => rule.operand === 'benfCd');
                            if (benfCdIndex !== -1) {
                              rules.splice(benfCdIndex, 1);
                            }
                            const benfMinAFYPIndex = rules.findIndex((rule) => rule.operand === 'benfMinAFYP');
                            if (benfMinAFYPIndex !== -1) {
                              rules.splice(benfMinAFYPIndex, 1);
                            }
                            formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'rules', value: rules } });
                          }
                        }}
                      />
                    }
                    label="All Benefits"
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isDisabled}
                        icon={<CircleUnchecked />}
                        checkedIcon={<CircleCheckedFilled />}
                        checked={!!benfCd}
                        onChange={(e) => {
                          if (e.target.checked && formState.rules && !benfCd) {
                            formDispatch({
                              type: 'MODIFY_FIELD',
                              payload: {
                                field: 'rules',
                                value: [
                                  ...formState.rules,
                                  {
                                    operand: 'benfCd',
                                    operator: 'in',
                                    type: IncentiveRuleType.VALUE,
                                    values: [],
                                  },
                                ],
                              },
                            });
                          }
                        }}
                      />
                    }
                    label="Specific Benefits"
                    labelPlacement="end"
                  />
                  <div style={{ flexGrow: 1 }}>
                    <TextField
                      disabled={isDisabled || !!!benfCd}
                      style={{ margin: '0 0 0 10px' }}
                      fullWidth
                      className={classes.inputMargin}
                      margin="dense"
                      variant="outlined"
                      placeholder={'Input benefit code separate by comma. e.g. BENF01,BENF02'}
                      value={benfCd ? benfCd.values.join(',') : ''}
                      onChange={(e) => {
                        const benfCode = e.target.value.replace(/[^0-9a-zA-Z,]/g, '');
                        if (formState.rules) {
                          let rules = [...formState.rules];
                          let benfRuleIndex = rules.findIndex((rule) => rule.operand === 'benfCd');
                          if (benfRuleIndex !== -1) {
                            rules[benfRuleIndex].values = benfCode ? benfCode.split(',') : [];
                            formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'rules', value: rules } });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                {benfCd && (
                  <div className={classes.rowContainer}>
                    <div className={classes.fieldContainer}>
                      <span className={classes.field}>Minimum AFYP per Benefit :</span>
                    </div>
                    <TextField
                      disabled={isDisabled}
                      style={{ margin: 0 }}
                      className={classes.inputMargin}
                      margin="dense"
                      variant="outlined"
                      value={benfMinAFYP ? benfMinAFYP.values[0] : ''}
                      onChange={(e) => {
                        const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                        let rules = formState.rules ? [...formState.rules] : [];
                        const benfMinAFYPIndex = rules.findIndex((rule) => rule.operand === 'benfMinAFYP');
                        if (benfMinAFYPIndex !== -1) {
                          if (!e.target.value) {
                            rules.splice(benfMinAFYPIndex, 1);
                          } else {
                            rules[benfMinAFYPIndex].values = [numberSeparator(onlyNums)];
                          }
                        } else {
                          if (e.target.value) {
                            rules = [
                              ...rules,
                              {
                                operand: 'benfMinAFYP',
                                operator: AwardOperatorType.GREATER_THAN_EQUAL,
                                type: IncentiveRuleType.CURRENCY,
                                values: [numberSeparator(onlyNums)],
                              },
                            ];
                          }
                        }
                        formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'rules', value: rules } });
                      }}
                    />
                  </div>
                )}
                <MiniIncentiveAwardTable
                  style={{ marginTop: 10 }}
                  errorState={{
                    requirementEmpty: errorState.mandatory.requirementEmpty,
                    awardFirstRowName: errorState.mandatory.awardFirstRowName,
                    awardFirstRowQuota: errorState.mandatory.awardFirstRowQuota,
                  }}
                  isDisabled={isDisabled}
                  incentiveAward={formState.awards}
                  onDismissError={onDismissErrorHandler}
                  onUpdate={(result) =>
                    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awards', value: result } })
                  }
                />
              </div>
            </>
          )}
        </div>

        {/* Publish Settings */}
        <div>
          <div className={classes.divideMargin}>
            <span className={classes.subHeader}>Publish Settings</span>
          </div>
          <div className={classes.textAreaRowContainer}>
            <div className={classes.checkboxFieldContainer}>
              <span className={classes.field}>Stick On Top :</span>
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.stickOnTop}
                  onChange={(e) =>
                    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'stickOnTop', value: e.target.checked } })
                  }
                />
              }
              label="Yes"
              labelPlacement="end"
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isDisabled}
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={!formState.stickOnTop}
                  onChange={(e) =>
                    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'stickOnTop', value: !e.target.checked } })
                  }
                />
              }
              label="No"
              labelPlacement="end"
            />
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Quota :</span>
            </div>
            <TextField
              disabled={isDisabled}
              style={{ width: 100 }}
              className={classes.inputMargin}
              margin="dense"
              variant="outlined"
              value={formState.quota}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'quota', value: onlyNums } });
              }}
            />
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Publish Date :</span>
            </div>
            <PruDateTimePicker
              ampm={false}
              disabled={isDisabled}
              format="DD/MM/YYYY HH:mm"
              value={formState.publishDate}
              onChange={(date) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'publishDate', value: date } })
              }
            />
          </div>
          <div style={{ marginBottom: 3 }} className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Archive Date :</span>
            </div>
            <PruDatePicker
              disabled={isDisabled}
              format="DD/MM/YYYY"
              value={formState.archiveDate}
              onChange={(date) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'archiveDate', value: date } })
              }
            />
          </div>
          <div style={{ marginBottom: 20 }} className={classes.textAreaRowContainer}>
            <div className={classes.checkboxFieldContainer}>
              <span className={classes.field}>Target Applicants :</span>
            </div>
            {channel == 'BROKER' && region == 'MO' ? (
              <TargetApplicationSection_MO_BROKER
                isDisabled={isDisabled}
                enrolmentType={formState.enrolmentType}
                eligibleDesignations={formState.eligibleDesignations || []}
                eligibleAgents={formState.eligibleAgents || []}
                excludedAgents={formState.excludedAgents || []}
                agentListFileName={formState.agentListFileName}
                onModifyTarget={modifyTargetApplicant}
                onToggleDesignation={(value) => {
                  formDispatch({ type: 'TOGGLE_DESIGNATION', payload: { value } });
                }}
              />
            ) : (
              <TargetApplicationSection
                isDisabled={isDisabled}
                enrolmentType={formState.enrolmentType}
                eligibleDesignations={formState.eligibleDesignations || []}
                eligibleAgents={formState.eligibleAgents || []}
                excludedAgents={formState.excludedAgents || []}
                agentListFileName={formState.agentListFileName}
                onModifyTarget={modifyTargetApplicant}
                onToggleDesignation={(value) => {
                  formDispatch({ type: 'TOGGLE_DESIGNATION', payload: { value } });
                }}
              />
            )}
          </div>
        </div>

        {/* Uploaded File Records */}
        <div className={classes.sectionMargin}>
          <div style={{ marginBottom: 15 }}>
            <span className={classes.subHeader}>Past Files Record</span>
          </div>
          <div style={{ marginBottom: 15 }} className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Bulletin File Name :</span>
            </div>
            <div style={{ flexGrow: 1 }}>{lastFileName.bulletinFileName || 'N/A'}</div>
          </div>
          <div style={{ marginBottom: 15 }} className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Result File Name :</span>
            </div>
            <div style={{ flexGrow: 1 }}>{lastFileName.resultFileName || 'N/A'}</div>
          </div>
          <div style={{ marginBottom: 15 }} className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>Agent List File Name :</span>
            </div>
            <div style={{ flexGrow: 1 }}>{lastFileName.agentListFileName || 'N/A'}</div>
          </div>
        </div>

        <div className={classes.footerContainer}>
          {isDisabled ? (
            <Button variant="contained" color="secondary" onClick={onUnpublish}>
              Unpublish
            </Button>
          ) : (
            <>
              <Button variant="contained" color="secondary" onClick={() => onSubmitForm(PublishStatus.DRAFT)}>
                Save as Draft
              </Button>
              <Button
                style={{ marginLeft: 25 }}
                variant="contained"
                color="secondary"
                onClick={() => onSubmitForm(PublishStatus.PUBLISHED)}
              >
                Publish
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default IncentiveDetailForm;
